import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditCard, Shield, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const PaymentPage = () => {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", amount: "" });
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email is required";
    if (!form.mobile.trim() || !/^[6-9]\d{9}$/.test(form.mobile.replace(/\s/g, "")))
      e.mobile = "Valid 10-digit mobile number required";
    const amt = parseFloat(form.amount);
    if (!form.amount || isNaN(amt) || amt < 1) e.amount = "Minimum amount is ₹1";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePayment = async () => {
    if (!validate()) return;
    setLoading(true);

    try {
      // Step 1: Create order
      const { data } = await axios.post(`${API_URL}/api/payments/create-order`, {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        amount: parseFloat(form.amount),
      });

      // Step 2: Open Razorpay modal
      const options = {
        key: RAZORPAY_KEY,
        amount: data.amount,
        currency: data.currency,
        name: "DNoTours",
        description: "Travel Payment",
        order_id: data.order_id,
        prefill: {
          name: form.name,
          email: form.email,
          contact: `+91${form.mobile.replace(/\s/g, "").replace(/^\+91/, "")}`,
        },
        theme: { color: "#7c3aed" },
        handler: async (response) => {
          // Step 3: Verify payment
          try {
            await axios.post(`${API_URL}/api/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaymentSuccess({
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              amount: form.amount,
            });
            toast.success("Payment successful!");
          } catch {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(response.error?.description || "Payment failed");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not initiate payment");
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-slate-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="max-w-md w-full shadow-xl border-green-200" data-testid="payment-success-card">
            <CardContent className="pt-10 pb-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
              <p className="text-slate-600 mb-6">Thank you for your payment of ₹{parseFloat(paymentSuccess.amount).toLocaleString()}</p>
              <div className="bg-slate-50 rounded-lg p-4 text-left text-sm space-y-2 mb-6">
                <div className="flex justify-between">
                  <span className="text-slate-500">Payment ID</span>
                  <span className="font-mono text-slate-800 text-xs">{paymentSuccess.paymentId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Order ID</span>
                  <span className="font-mono text-slate-800 text-xs">{paymentSuccess.orderId}</span>
                </div>
              </div>
              <Link to="/">
                <Button className="w-full" data-testid="back-home-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50 pt-28 pb-16 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CreditCard className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Make a Payment</h1>
          <p className="text-slate-600">Complete your payment securely via Razorpay</p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="shadow-xl border-slate-200" data-testid="payment-form-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Payment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="payment-name-input"
                />
                {errors.name && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{errors.name}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="payment-email-input"
                />
                {errors.email && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{errors.email}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  type="tel"
                  placeholder="9876543210"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  data-testid="payment-mobile-input"
                />
                {errors.mobile && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{errors.mobile}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="amount">Amount (₹) *</Label>
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="Enter amount in INR"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="text-lg font-semibold"
                  data-testid="payment-amount-input"
                />
                {errors.amount && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{errors.amount}</p>}
              </div>

              <Button
                onClick={handlePayment}
                disabled={loading}
                className="w-full h-12 text-base bg-purple-600 hover:bg-purple-700"
                data-testid="pay-now-btn"
              >
                {loading ? (
                  "Processing..."
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    Pay ₹{form.amount ? parseFloat(form.amount).toLocaleString() : "0"}
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-2">
                <Shield className="h-3.5 w-3.5" />
                <span>Secured by Razorpay. 100% safe &amp; encrypted.</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentPage;
